import {
  AccessApi,
  Group,
  GroupCommon,
  PaginatedResponse,
  Permission,
  Role,
  RoleCommon,
  User,
} from "decodable-sdk-js/v1alpha2";

/**
 * MockAccessApi implements a mock access api against in-memory data.
 *
 * The MockAccessApi mocks the service dynamically, rather than only
 * returning static data (or throwing "method not implemented") for
 * every endpoint. This provides a more realistic mock for manual
 * interaction testing. Tests should still mock the endpoints they
 * care about, especially when testing error conditions, to control
 * the test preconditions precisely rather than relying on globally-
 * defined behavior.
 */
export class MockAccessApi implements AccessApi {
  private db: ReturnType<typeof newMockDatabase>;

  constructor({ db }: { db?: MockAccessDatabase } = {}) {
    this.db = db || newMockDatabase();
    (window as any).accessData = this.db;
  }

  async listGroups(): Promise<PaginatedResponse<Group>> {
    return paginated([...this.db.groups.values()]);
  }

  async createGroup(params: { group: GroupCommon }): Promise<Group> {
    const now = new Date().toISOString();
    const group: Group = {
      id: nextGroupId(),
      ...params.group,
      create_time: now,
      update_time: now,
    };
    this.db.groups.set(group.id, group);
    return group;
  }

  async getGroup(params: { id: string }): Promise<Group> {
    return find(() => this.db.groups.get(params.id), `Group ${params.id}`);
  }

  async updateGroup(params: {
    group: GroupCommon & { id: string };
  }): Promise<Group> {
    const updated = {
      ...this.db.groups.get(params.group.id),
      ...params.group,
    };
    this.db.groups.set(params.group.id, updated);
    return updated;
  }

  async deleteGroup(params: { id: string }): Promise<void> {
    this.db.groups.delete(params.id);
    this.db.group_roles = this.db.group_roles.filter(
      (edge) => edge.groupId != params.id
    );
    this.db.user_groups = this.db.user_groups.filter(
      (edge) => edge.groupId != params.id
    );
  }

  async listGroupUsers(params: {
    id: string;
  }): Promise<PaginatedResponse<User>> {
    return paginated(
      this.db.user_groups
        .filter((edge) => edge.groupId == params.id)
        .map((edge) => this.db.users.get(edge.userId))
    );
  }

  async addGroupUser(params: {
    groupId: string;
    userId: string;
  }): Promise<Group> {
    this.db.user_groups.push(params);
    return this.db.groups.get(params.groupId);
  }

  async removeGroupUser(params: {
    groupId: string;
    userId: string;
  }): Promise<Group> {
    this.db.user_groups = this.db.user_groups.filter(
      (e) => !(e.groupId == params.groupId && e.userId == params.userId)
    );
    return this.db.groups.get(params.groupId);
  }

  async listGroupRoles(params: {
    id: string;
  }): Promise<PaginatedResponse<Role>> {
    return paginated(
      this.db.group_roles
        .filter((edge) => edge.groupId == params.id)
        .map((edge) => this.db.roles.get(edge.roleId))
    );
  }

  async addGroupRole(params: {
    groupId: string;
    roleId: string;
  }): Promise<Group> {
    this.db.group_roles.push(params);
    return this.db.groups.get(params.groupId);
  }

  async removeGroupRole(params: {
    groupId: string;
    roleId: string;
  }): Promise<Group> {
    this.db.group_roles = this.db.group_roles.filter(
      (e) => !(e.groupId == params.groupId && e.roleId == params.roleId)
    );
    return this.db.groups.get(params.groupId);
  }

  async listRoles(): Promise<PaginatedResponse<Role>> {
    return paginated([...this.db.roles.values()]);
  }

  async getRole(params: { id: string }): Promise<Role> {
    return find(() => this.db.roles.get(params.id), `Role ${params.id}`);
  }

  async createRole(params: { role: RoleCommon }): Promise<Role> {
    const now = new Date().toISOString();
    const role: Role = {
      id: nextRoleId(),
      ...params.role,
      create_time: now,
      update_time: now,
    };
    this.db.roles.set(role.id, role);
    this.db.role_permissions.set(role.id, []);
    return role;
  }

  async updateRole(params: {
    role: RoleCommon & {
      id: string;
      create_time: string;
      update_time: string;
    } & { id: string };
  }): Promise<Role> {
    const updated = {
      ...this.db.roles.get(params.role.id),
      ...params.role,
    };
    this.db.roles.set(params.role.id, updated);
    return updated;
  }

  async deleteRole(params: { id: string }): Promise<void> {
    this.db.roles.delete(params.id);
    this.db.group_roles = this.db.group_roles.filter(
      (edge) => edge.roleId != params.id
    );
  }

  async listRoleGroups(params: {
    id: string;
  }): Promise<PaginatedResponse<Group>> {
    return paginated(
      this.db.group_roles
        .filter((edge) => edge.roleId == params.id)
        .map((edge) => this.db.groups.get(edge.groupId))
    );
  }

  async listRolePermissions(params: {
    id: string;
  }): Promise<PaginatedResponse<Permission>> {
    return paginated(this.db.role_permissions.get(params.id));
  }

  async setRolePermissions(params: {
    id: string;
    permissions: Permission[];
  }): Promise<PaginatedResponse<Permission>> {
    this.db.role_permissions.set(params.id, params.permissions);
    return paginated(params.permissions);
  }

  async listUsers(): Promise<PaginatedResponse<User>> {
    return paginated([...this.db.users.values()]);
  }

  async getUser(params: { id: string }): Promise<User> {
    return this.db.users.get(params.id);
  }

  async getUserGroups(params: {
    id: string;
  }): Promise<PaginatedResponse<Group>> {
    return paginated(
      this.db.user_groups
        .filter((edge) => edge.userId == params.id)
        .map((edge) => this.db.groups.get(edge.groupId))
    );
  }
}

function find<T>(finder: () => T, description?: string): T {
  const result = finder();
  if (!result) {
    throw {
      status: 404,
      message: `Not found: ${description ?? "<no description>"}`,
    };
  }
  return result;
}

function paginated(items: any[]) {
  return { items, next_page_token: "unused" };
}

function idGenerator() {
  let next = 0;
  return function () {
    return (++next).toString();
  };
}

const nextGroupId = idGenerator();
const nextRoleId = idGenerator();
const nextUserId = idGenerator();

export type MockAccessDatabase = ReturnType<typeof newMockDatabase>;

/**
 * Generates mock access control data for testing.
 */
export function newMockDatabase() {
  if (process.env.NODE_ENV === "production") {
    // https://parceljs.org/features/production/#development-branch-removal
    throw new Error("The mock access data is stripped in the production build");
  } else {
    const esammerUserId = nextUserId();

    // Members of #team-eng as on March 13th 2023
    const users: User[] = [
      {
        id: esammerUserId,
        name: "Eric Sammer",
        email: "esammer@decodable.co",
        given_name: "Eric",
        family_name: "Sammer",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-02-21/3149795235121_70f29fa11426239c6a04_original.jpg",
        create_time: "2021-01-01T00:00:00.000Z",
      },
      {
        id: nextUserId(),
        name: "David Fabritius",
        email: "david@decodable.co",
        given_name: "David",
        family_name: "Fabritius",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-09-26/4129844608613_eef6ebaae034bc771c91_original.jpg",
        create_time: "2023-03-10T23:04:59.000Z",
      },
      {
        id: nextUserId(),
        name: "Gunnar Morling",
        email: "gunnar@decodable.co",
        given_name: "Gunnar",
        family_name: "Morling",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-11-01/4291034135191_efae6e6e12f77b968ad8_original.jpg",
        create_time: "2023-02-15T17:14:21.000Z",
      },
      {
        id: nextUserId(),
        name: "Jared Breeden",
        email: "jbreeden@decodable.co",
        given_name: "Jared",
        family_name: "Breeden",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2021-08-20/2388455835063_1f34eda058ea765084c2_original.png",
        create_time: "2023-03-11T05:02:04.000Z",
      },
      {
        id: nextUserId(),
        name: "Joel McKelvey",
        email: "joel@decodable.co",
        given_name: "Joel",
        family_name: "McKelvey",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2023-02-27/4865698311186_b5b4a402edc3f7b610b6_original.png",
        create_time: "2023-02-27T16:12:48.000Z",
      },
      {
        id: nextUserId(),
        name: "John MacKinnon",
        email: "jmackinnon@decodable.co",
        given_name: "John",
        family_name: "MacKinnon",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-07-05/3774098827873_40fbacbacb1a346b91a8_original.png",
        create_time: "2023-03-06T18:43:17.000Z",
      },
      {
        id: nextUserId(),
        name: "Josh Mahonin",
        email: "jmahonin@decodable.co",
        given_name: "Josh",
        family_name: "Mahonin",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2021-08-23/2406018260405_7d8381c324e1397c34ba_original.png",
        create_time: "2023-03-11T05:02:05.000Z",
      },
      {
        id: nextUserId(),
        name: "Maria Matsukawa",
        email: "maria@decodable.co",
        given_name: "Maria",
        family_name: "Matsukawa",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2023-01-09/4599639774023_b7d5efa3f20aadfecc31_original.jpg",
        create_time: "2023-03-11T05:03:09.000Z",
      },
      {
        id: nextUserId(),
        name: "Marika Goodrich",
        email: "marika@decodable.co",
        given_name: "Marika",
        family_name: "Goodrich",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-10-31/4302635269362_45d8d140ce48f3051140_original.png",
        create_time: "2023-03-11T08:02:27.000Z",
      },
      {
        id: nextUserId(),
        name: "Matt",
        email: "matt@decodable.co",
        given_name: "Matt",
        family_name: "",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2021-06-02/2137982352228_7fd3b08f070b687ad4d1_original.jpg",
        create_time: "2023-03-09T19:03:59.000Z",
      },
      {
        id: nextUserId(),
        name: "Max Feng",
        email: "maxf@decodable.co",
        given_name: "Max",
        family_name: "Feng",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2021-09-07/2470092566852_5b4176f48480e9596733_original.jpg",
        create_time: "2023-02-21T16:49:45.000Z",
      },
      {
        id: nextUserId(),
        name: "Nico Weidner",
        email: "nico@decodable.co",
        given_name: "Nico",
        family_name: "Weidner",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2023-02-01/4728921088963_f6d61ba901386a34dd14_original.jpg",
        create_time: "2023-01-31T23:14:07.000Z",
      },
      {
        id: nextUserId(),
        name: "Riley Johnson",
        email: "riley@decodable.co",
        given_name: "Riley",
        family_name: "Johnson",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-03-07/3200320262117_a6c79c6ab6fce74a184d_original.jpg",
        create_time: "2023-03-12T00:46:07.000Z",
      },
      {
        id: nextUserId(),
        name: "Robert Metzger",
        email: "rmetzger@decodable.co",
        given_name: "Robert",
        family_name: "Metzger",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-10-27/4282516496195_955aeaa3f4f2d57836fa_original.jpg",
        create_time: "2023-03-11T07:43:52.000Z",
      },
      {
        id: nextUserId(),
        name: "Saketh Kurnool",
        email: "sak@decodable.co",
        given_name: "Saketh",
        family_name: "Kurnool",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-06-07/3648240459185_8ae47268be09025e518d_original.jpg",
        create_time: "2023-03-10T23:59:13.000Z",
      },
      {
        id: nextUserId(),
        name: "Sandeep Shrestha",
        email: "sandeep@decodable.co",
        given_name: "Sandeep",
        family_name: "Shrestha",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-02-07/3091462850384_036238d22ff7f03ea4d9_original.jpg",
        create_time: "2023-03-03T00:34:08.000Z",
      },
      {
        id: nextUserId(),
        name: "Saul Alvarez",
        email: "saul.alvarez@decodable.co",
        given_name: "Saul",
        family_name: "Alvarez",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-08-08/3910941633042_4c3bbb053e712cf4f37f_original.png",
        create_time: "2023-03-11T08:01:00.000Z",
      },
      {
        id: nextUserId(),
        name: "Sharon Xie",
        email: "rxie@decodable.co",
        given_name: "Sharon",
        family_name: "Xie",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2021-03-22/1892278535396_6d9cf9dd484828f28a95_original.jpg",
        create_time: "2023-03-11T08:01:48.000Z",
      },
      {
        id: nextUserId(),
        name: "Simohamed",
        email: "simo@decodable.co",
        given_name: "Simohamed",
        family_name: "",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2022-06-27/3736264094673_bd72894e5d05e82dabb3_original.jpg",
        create_time: "2023-02-27T19:24:40.000Z",
      },
      {
        id: nextUserId(),
        name: "Timothy James",
        email: "tjames@decodable.co",
        given_name: "Timothy",
        family_name: "James",
        picture:
          "https://s3-us-west-2.amazonaws.com/slack-files2/avatars/2021-03-15/1861731263522_58697f5976e3bbf012cd_original.jpg",
        create_time: "2023-01-04T07:59:15.000Z",
      },
    ];

    const adminsGroupId = nextGroupId();
    const newUsersGroupId = nextGroupId();
    const emptyGroupId = nextGroupId();

    const groups: Group[] = [
      {
        id: adminsGroupId,
        name: "admins",
        create_time: "2022-01-01T00:00:00.000Z",
        update_time: "2022-01-01T00:00:00.000Z",
      },
      {
        id: newUsersGroupId,
        name: "new-users",
        create_time: "2022-01-01T00:00:00.000Z",
        update_time: "2022-01-01T00:00:00.000Z",
      },
      {
        id: emptyGroupId,
        name: "empty-group",
        create_time: "2022-01-01T00:00:00.000Z",
        update_time: "2022-01-01T00:00:00.000Z",
      },
    ];

    const adminRoleId = nextRoleId();
    const newUserRoleId = nextRoleId();
    const emptyRoleId = nextRoleId();

    const roles: Role[] = [
      {
        id: adminRoleId,
        name: "admin",
        create_time: "2022-01-01T00:00:00.000Z",
        update_time: "2022-01-01T00:00:00.000Z",
      },
      {
        id: newUserRoleId,
        name: "new-user",
        create_time: "2022-01-01T00:00:00.000Z",
        update_time: "2022-01-01T00:00:00.000Z",
      },
      {
        id: emptyRoleId,
        name: "empty-role",
        create_time: "2022-01-01T00:00:00.000Z",
        update_time: "2022-01-01T00:00:00.000Z",
      },
    ];

    const group_roles: { groupId: string; roleId: string }[] = [
      { groupId: adminsGroupId, roleId: adminRoleId },
      { groupId: newUsersGroupId, roleId: newUserRoleId },
    ];

    const user_groups: { userId: string; groupId: string }[] = [
      { groupId: adminsGroupId, userId: esammerUserId },
      ...users.map((u) => ({ groupId: newUsersGroupId, userId: u.id })),
    ];

    const role_permissions: Map<string, Permission[]> = new Map([
      [adminRoleId, [{ effect: "allow", pattern: "*:*:*" }]],
      [newUserRoleId, [{ effect: "allow", pattern: "*:*:*" }]],
      [emptyRoleId, []],
    ]);

    return {
      users: new Map(users.map((u) => [u.id, u])),
      groups: new Map(groups.map((g) => [g.id, g])),
      roles: new Map(roles.map((r) => [r.id, r])),
      user_groups,
      group_roles,
      role_permissions,
    };
  }
}
